import {
  FormControl,
  FormControlProps,
  FormHelperText,
  FormLabel,
  Box,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ReactNode } from 'react';
import { B2B_FIELD_LABEL_TO_CONTROL_GAP } from './b2bFormControlTokens';
import { colors } from 'styles/colors';
import { pxToRem } from 'styles/pxToRem';

/** Reserved height under controls so validation messages don’t shift fields below (≈2× body small line height). */
export const B2B_FIELD_ERROR_RESERVED_MIN_H = '2.75rem';

export type B2bFieldLayoutProps = Omit<FormControlProps, 'children' | 'label'> & {
  fieldId: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
};

export const B2bFieldLayout = ({
  fieldId,
  label,
  hint,
  error,
  children,
  isInvalid: isInvalidProp,
  ...formControlProps
}: B2bFieldLayoutProps) => {
  const isInvalid = isInvalidProp ?? Boolean(error);

  return (
    <FormControl isInvalid={isInvalid} textAlign='right' w='full' {...formControlProps}>
      <VStack spacing={B2B_FIELD_LABEL_TO_CONTROL_GAP} align='stretch' w='full'>
        <FormLabel
          htmlFor={fieldId}
          fontFamily="'Noto Sans Arabic', 'Noto Sans', sans-serif"
          fontWeight='600'
          fontStyle='normal'
          fontSize={{ base: pxToRem(16), lg: pxToRem(20) }}
          lineHeight={{ base: pxToRem(28), lg: pxToRem(46.31) }}
          letterSpacing='0'
          color='dark.D800'
          w='full'
          textAlign='right'
          verticalAlign='middle'
          m={0}
          sx={{
            leadingTrim: 'none',
            '& span[role="presentation"]': {
              fontFamily: "'Noto Sans Arabic', 'Noto Sans', sans-serif",
              fontWeight: '600',
              fontStyle: 'normal',
              fontSize: { base: pxToRem(11), lg: pxToRem(12) },
              lineHeight: { base: pxToRem(28), lg: pxToRem(46.31) },
              letterSpacing: '0',
              textAlign: 'right',
              verticalAlign: 'middle',
              leadingTrim: 'none',
              color: colors.red,
            },
          }}
        >
          {label}
        </FormLabel>
        {hint != null && hint !== false && (
          <FormHelperText
            id={`${fieldId}-helper`}
            textStyle='bodySmallRegular'
            color='dark.D500'
            m={0}
            textAlign='right'
          >
            {hint}
          </FormHelperText>
        )}
        <Box w='full' maxW='100%'>
          {children}
        </Box>
        <Box
          id={`${fieldId}-error-slot`}
          minH={B2B_FIELD_ERROR_RESERVED_MIN_H}
          m={0}
          mt={0}
          w='full'
          textAlign='right'
        >
          {error != null && error !== false ? (
            <Text
              id={`${fieldId}-error`}
              textStyle='bodySmallRegular'
              color={colors.red}
              role='alert'
            >
              {error}
            </Text>
          ) : null}
        </Box>
      </VStack>
    </FormControl>
  );
};
